import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { CLOUD_API_URL } from '../config/serverConfig';
import { useTranslation } from '../i18n/useTranslation';

const GOLD = '#C9A84C';

function getAuthToken() {
  try {
    const stored = localStorage.getItem('maestro_auth');
    return stored ? JSON.parse(stored).accessToken : null;
  } catch {}
  return null;
}

export default function TrackContextMenu({
  track,
  album,
  position,
  onClose,
  onPlayNow,
  onPlayNext,
  onPlayOnlyThisTrack,
  onAddToQueue,
  playlists = [],
  onAddToPlaylist,
  onCreatePlaylist,
  onGoToArtist,
  onGoToAlbum,
  onToggleFavorite,
  isFav,
  onRemoveFromPlaylist,
}) {
  const { t } = useTranslation();
  const [subView, setSubView] = useState(null);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [toastMsg, setToastMsg] = useState(null);
  const [creating, setCreating] = useState(false);
  const menuRef = useRef(null);
  const [menuHeight, setMenuHeight] = useState(460);

  const trackTitle = track?.title || 'Unknown Track';
  const trackArtist = track?.artistName || track?.artist_name || track?.artist || '';
  const coverUrl = album?.coverUrl || album?.cover_url || album?.imageUrl || album?.image?.large || null;

  useEffect(() => {
    if (menuRef.current) {
      setMenuHeight(menuRef.current.offsetHeight);
    }
  });

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose?.();
    };
    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [onClose]);

  const menuW = 280;
  const menuStyle = (() => {
    const x = position?.x ?? 200;
    const y = position?.y ?? 200;
    const estH = subView === 'playlists' ? 340 : menuHeight;
    const left = Math.max(8, Math.min(x - menuW, window.innerWidth - menuW - 8));
    let top = y - estH - 4;
    if (top < 8) top = y + 36;
    return { left, top: Math.max(8, Math.min(top, window.innerHeight - estH - 8)) };
  })();

  const showToast = (msg) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 2500);
  };

  const handleBan = async () => {
    const token = getAuthToken();
    if (!token) { showToast('Please sign in'); return; }
    try {
      await fetch(`${CLOUD_API_URL}/api/user/preferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action: 'ban_track', track_id: String(track?.id || ''), metadata: { title: trackTitle, artist: trackArtist } }),
      });
      showToast('Track banned');
      setTimeout(() => onClose?.(), 1200);
    } catch {
      showToast('Failed');
    }
  };

  const handleAddToLibrary = async () => {
    const token = getAuthToken();
    if (!token) { showToast('Please sign in'); return; }
    try {
      await fetch(`${CLOUD_API_URL}/api/user/library`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ type: 'track', trackId: String(track?.id || ''), albumId: String(album?.id || album?.serviceAlbumId || ''), metadata: { title: trackTitle, artist: trackArtist, coverUrl } }),
      });
      showToast('Added to library');
    } catch {
      showToast('Failed');
    }
  };

  const handleListenLater = async () => {
    const token = getAuthToken();
    if (!token) { showToast('Please sign in'); return; }
    try {
      await fetch(`${CLOUD_API_URL}/api/user/listen-later`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ type: 'track', trackId: String(track?.id || ''), albumId: String(album?.id || album?.serviceAlbumId || ''), metadata: { title: trackTitle, artist: trackArtist, coverUrl } }),
      });
      showToast('Added to Listen Later');
    } catch {
      showToast('Failed');
    }
  };

  const iconColor = 'var(--text-secondary, rgba(255,255,255,0.5))';

  const ActionIcon = ({ children, label, onClick: click }) => (
    <button
      onClick={click}
      style={{
        flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
        padding: '10px 4px', background: 'transparent', border: 'none', cursor: 'pointer',
        color: 'var(--text-primary)', fontSize: 10, fontWeight: 600, letterSpacing: '0.02em',
        transition: 'opacity 0.15s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.7'; }}
      onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
    >
      <div style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', color: iconColor }}>
        {children}
      </div>
      <span style={{ lineHeight: 1.2, textAlign: 'center' }}>{label}</span>
    </button>
  );

  const MenuItem = ({ icon, label, onClick: click, danger }) => (
    <button
      onClick={click}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '10px 16px', fontSize: 13, fontWeight: 500,
        color: danger ? '#ef4444' : 'var(--text-primary)',
        background: 'transparent', border: 'none', width: '100%',
        textAlign: 'left', cursor: 'pointer', transition: 'background 0.12s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-secondary, rgba(255,255,255,0.05))'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      <span style={{ width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', color: danger ? '#ef4444' : iconColor, flexShrink: 0 }}>
        {icon}
      </span>
      {label}
    </button>
  );

  const playlistView = (
    <>
      <button
        onClick={() => setSubView(null)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px',
          fontWeight: 600, fontSize: 13, color: 'var(--text-primary)',
          background: 'transparent', border: 'none', width: '100%', textAlign: 'left',
          cursor: 'pointer', borderBottom: '1px solid var(--border, rgba(255,255,255,0.1))',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-secondary, rgba(255,255,255,0.05))'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6"/></svg>
        Add to Playlist
      </button>
      <div style={{ padding: '8px 12px', display: 'flex', gap: 6 }}>
        <input
          value={newPlaylistName}
          onChange={(e) => setNewPlaylistName(e.target.value)}
          placeholder="New playlist name..."
          disabled={creating}
          style={{
            flex: 1, padding: '7px 10px', fontSize: 12, borderRadius: 6,
            border: '1px solid var(--border, rgba(255,255,255,0.15))',
            background: 'var(--bg-secondary, rgba(255,255,255,0.05))',
            color: 'var(--text-primary, #fff)', outline: 'none',
            opacity: creating ? 0.5 : 1,
          }}
          onKeyDown={async (e) => {
            if (e.key === 'Enter' && newPlaylistName.trim() && !creating) {
              const name = newPlaylistName.trim();
              setCreating(true);
              try {
                await onCreatePlaylist?.(name, track);
                showToast(`Added to "${name}"`);
              } catch { showToast('Failed to create playlist'); }
              setCreating(false);
              setNewPlaylistName('');
              setTimeout(() => onClose?.(), 1200);
            }
          }}
        />
        <button
          disabled={creating || !newPlaylistName.trim()}
          onClick={async () => {
            if (newPlaylistName.trim() && !creating) {
              const name = newPlaylistName.trim();
              setCreating(true);
              try {
                await onCreatePlaylist?.(name, track);
                showToast(`Added to "${name}"`);
              } catch { showToast('Failed to create playlist'); }
              setCreating(false);
              setNewPlaylistName('');
              setTimeout(() => onClose?.(), 1200);
            }
          }}
          style={{
            padding: '6px 10px', borderRadius: 6, background: GOLD,
            color: '#1a1a2e', fontSize: 11, fontWeight: 700, border: 'none', cursor: 'pointer',
            opacity: (creating || !newPlaylistName.trim()) ? 0.5 : 1,
          }}
        >
          {creating ? 'Creating...' : 'Create'}
        </button>
      </div>
      <div style={{ maxHeight: 220, overflowY: 'auto' }}>
        {playlists.map((pl) => (
          <button
            key={pl.id}
            onClick={async () => {
              try {
                await onAddToPlaylist?.(pl.id, track);
                showToast(`Added to "${pl.name}"`);
                setTimeout(() => onClose?.(), 1200);
              } catch { showToast('Failed to add track'); setTimeout(() => onClose?.(), 1200); }
            }}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '9px 16px',
              fontSize: 13, color: 'var(--text-primary)', background: 'transparent',
              border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-secondary, rgba(255,255,255,0.05))'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ color: iconColor, flexShrink: 0 }}>
              <path d="M15 6H3v2h12V6zm0 4H3v2h12v-2zM3 16h8v-2H3v2zM17 6v8.18c-.31-.11-.65-.18-1-.18-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3V8h3V6h-5z"/>
            </svg>
            {pl.name}
          </button>
        ))}
        {playlists.length === 0 && (
          <div style={{ padding: '12px 16px', color: 'var(--text-secondary)', fontSize: 12, textAlign: 'center' }}>No playlists yet</div>
        )}
      </div>
    </>
  );

  const mainView = (
    <>
      {/* Header: thumbnail + track info */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
        borderBottom: '1px solid var(--border, rgba(255,255,255,0.1))',
      }}>
        {coverUrl ? (
          <img
            src={coverUrl} alt=""
            style={{ width: 40, height: 40, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }}
          />
        ) : (
          <div style={{
            width: 40, height: 40, borderRadius: 6, flexShrink: 0,
            background: 'var(--bg-secondary, rgba(255,255,255,0.1))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--text-secondary)', fontSize: 16,
          }}>
            ♪
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 13, fontWeight: 600, color: 'var(--text-primary)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {trackTitle}
          </div>
          {trackArtist && (
            <div style={{
              fontSize: 11, color: 'var(--text-secondary)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 1,
            }}>
              {trackArtist}
            </div>
          )}
        </div>
      </div>

      {/* Action grid: 3 quick actions */}
      <div style={{
        display: 'flex', borderBottom: '1px solid var(--border, rgba(255,255,255,0.1))',
        padding: '4px 0',
      }}>
        <ActionIcon label="Add to library" onClick={handleAddToLibrary}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-1 9h-4v4h-2v-4H9V9h4V5h2v4h4v2z"/></svg>
        </ActionIcon>
        <ActionIcon label="Add to playlist" onClick={() => setSubView('playlists')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M14 10H2v2h12v-2zm0-4H2v2h12V6zm4 8v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zM2 16h8v-2H2v2z"/></svg>
        </ActionIcon>
        <ActionIcon label="Listen Later" onClick={handleListenLater}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm4.2 14.2L11 13V7h1.5v5.2l4.5 2.7-.8 1.3z"/></svg>
        </ActionIcon>
      </div>

      {/* Action list */}
      <div>
        {onPlayNow && (
          <MenuItem
            label={t('trackMenu.playNow') || 'Play Now'}
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>}
            onClick={() => { onPlayNow?.(track); showToast('Playing now'); setTimeout(() => onClose?.(), 600); }}
          />
        )}
        {onPlayNext && (
          <MenuItem
            label={t('trackMenu.playNext') || 'Play Next'}
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z"/></svg>}
            onClick={() => { onPlayNext?.(track); showToast('Playing next'); setTimeout(() => onClose?.(), 600); }}
          />
        )}
        {onPlayOnlyThisTrack && (
          <MenuItem
            label={t('trackMenu.playOnlyThisTrack') || 'Play only this track'}
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5V8l6 4-6 4.5z"/></svg>}
            onClick={() => { onPlayOnlyThisTrack?.(track); showToast(t('trackMenu.playOnlyThisTrack') || 'Play only this track'); setTimeout(() => onClose?.(), 600); }}
          />
        )}
        <MenuItem
          label={t('trackMenu.addToQueue') || 'Add to Queue'}
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M15 6H3v2h12V6zm0 4H3v2h12v-2zM3 16h8v-2H3v2zM17 6v8.18c-.31-.11-.65-.18-1-.18-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3V8h3V6h-5z"/></svg>}
          onClick={() => { onAddToQueue?.(track); showToast('Added to queue'); setTimeout(() => onClose?.(), 800); }}
        />
        <MenuItem
          label={isFav ? 'Remove from Favorites' : 'Add to Favorites'}
          icon={
            isFav
              ? <svg width="16" height="16" viewBox="0 0 24 24" fill={GOLD}><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>
              : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>
          }
          onClick={() => { onToggleFavorite?.(); showToast(isFav ? 'Removed from favorites' : 'Added to favorites'); }}
        />
        {onRemoveFromPlaylist && (
          <MenuItem
            label={t('trackMenu.removeFromPlaylist')}
            danger
            icon={
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14zM10 11v6M14 11v6" />
              </svg>
            }
            onClick={async () => {
              try {
                await onRemoveFromPlaylist(track);
                showToast('Removed from playlist');
                setTimeout(() => onClose?.(), 800);
              } catch {
                showToast('Could not remove track');
              }
            }}
          />
        )}

        <div style={{ borderTop: '1px solid var(--border, rgba(255,255,255,0.1))', margin: '2px 0' }} />

        <MenuItem
          label="View credits"
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zM6 20V4h7v5h5v11H6z"/></svg>}
          onClick={() => { showToast('Credits coming soon'); }}
        />
        {trackArtist && (
          <MenuItem
            label="Go to Artist"
            icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>}
            onClick={() => { onGoToArtist?.(trackArtist); onClose?.(); }}
          />
        )}
        <MenuItem
          label="Go to Album"
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 14.5c-2.49 0-4.5-2.01-4.5-4.5S9.51 7.5 12 7.5s4.5 2.01 4.5 4.5-2.01 4.5-4.5 4.5zm0-5.5c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1z"/></svg>}
          onClick={() => { onGoToAlbum?.(album); onClose?.(); }}
        />
        <MenuItem
          label="Copy track info"
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>}
          onClick={() => {
            navigator.clipboard?.writeText(`${trackTitle} by ${trackArtist}`).then(() => showToast('Copied to clipboard')).catch(() => {});
            setTimeout(() => onClose?.(), 800);
          }}
        />
      </div>
    </>
  );

  return ReactDOM.createPortal(
    <>
      <div
        ref={menuRef}
        style={{
          position: 'fixed',
          left: menuStyle.left,
          top: menuStyle.top,
          zIndex: 9999,
          width: menuW,
          background: 'var(--bg-card, #1e1e30)',
          border: '1px solid var(--border, rgba(255,255,255,0.12))',
          borderRadius: 12,
          boxShadow: '0 12px 40px rgba(0,0,0,0.45), 0 2px 8px rgba(0,0,0,0.2)',
          overflow: 'hidden',
          animation: 'popIn 0.15s ease',
        }}
      >
        {subView === 'playlists' ? playlistView : mainView}
      </div>
      {toastMsg && (
        <div style={{
          position: 'fixed', bottom: 100, left: '50%', transform: 'translateX(-50%)',
          background: 'var(--bg-card)', color: 'var(--text-primary)', padding: '10px 20px', borderRadius: 10,
          fontSize: 13, fontWeight: 600, zIndex: 10000,
          boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
          border: '1px solid var(--border)',
          animation: 'popIn 0.12s ease',
        }}>
          {toastMsg}
        </div>
      )}
    </>,
    document.body
  );
}
